import { toTitleCase } from '@dispatch/shared';
import { isRunningInJest } from '@dispatch/testing';
import {
  tableStyleHideSeparator,
  tableStyleOverrides,
  MUI_X_5_GRID_PROPS,
  useGridApiRefKeepingPage,
} from '@dispatch/web/ui';
import {
  Button,
  Card,
  CardContent,
  CardHeader,
  IconButton,
  Stack,
  TextField,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import SearchIcon from '@mui/icons-material/Search';
import {
  GridColDef,
  GridInitialState,
  GridValidRowModel,
  QuickFilter,
  QuickFilterClear,
  QuickFilterControl,
} from '@mui/x-data-grid';
import { DataGridPro } from '@mui/x-data-grid-pro';
import { styled } from '@mui/material/styles';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';

// Row dividers are the only rules unique to this table; everything else reuses
// the shared `tableStyleOverrides` / `tableStyleHideSeparator` (composed via the
// sx array below) instead of re-declaring them. Divider color follows
// palette.mode; light keeps the original MUI grey.
const rowDividerOverrides = (theme: Theme): SystemStyleObject<Theme> => ({
  '.MuiDataGrid-cell': {
    borderBottom: 'none !important',
  },
  '.MuiDataGrid-row': {
    borderBottom: `1px solid ${
      theme.palette.mode === 'dark'
        ? theme.palette.divider
        : 'rgba(224, 224, 224, 1)'
    }`,
  },
  '.MuiDataGrid-row:last-child': {
    borderBottom: 'none !important',
  },
});

const INITIAL_STATE: GridInitialState = {
  pagination: { paginationModel: { pageSize: 5 } },
};

const ExpandedQuickFilterRoot = styled('div')(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  flexWrap: 'wrap',
  gap: theme.spacing(1),
  padding: theme.spacing(0.5, 0.5, 0),
}));

const SearchField = styled(TextField)(({ theme }) => ({
  width: 'auto',
  paddingBottom: theme.spacing(0.5),
  '& input': {
    marginLeft: theme.spacing(0.5),
  },
  '& .MuiInput-underline:before': {
    borderBottom: `1px solid ${theme.palette.divider}`,
  },
  [`& input[type="search"]::-webkit-search-decoration,
  & input[type="search"]::-webkit-search-cancel-button,
  & input[type="search"]::-webkit-search-results-button,
  & input[type="search"]::-webkit-search-results-decoration`]: {
    display: 'none',
  },
}));

const CLEAR_HIDDEN = { visibility: 'hidden' } as const;
const CLEAR_VISIBLE = { visibility: 'visible' } as const;

const CLEAR_BUTTON = (
  <QuickFilterClear
    render={({ onClick }, state) => (
      <IconButton
        aria-label="Clear"
        size="small"
        sx={state.value ? CLEAR_VISIBLE : CLEAR_HIDDEN}
        onClick={onClick}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    )}
  />
);

function ExpandedQuickFilterToolbar({ placeholder }: { placeholder: string }) {
  return (
    <ExpandedQuickFilterRoot>
      <QuickFilter expanded>
        <QuickFilterControl
          render={({ ref, value, onChange, onKeyDown, slotProps }) => (
            <SearchField
              variant="standard"
              type="search"
              aria-label="Search"
              placeholder={placeholder}
              inputRef={ref}
              value={value}
              onChange={onChange}
              onKeyDown={onKeyDown}
              slotProps={{
                htmlInput: slotProps?.htmlInput,
                input: {
                  startAdornment: <SearchIcon fontSize="small" />,
                  endAdornment: CLEAR_BUTTON,
                },
              }}
            />
          )}
        />
      </QuickFilter>
    </ExpandedQuickFilterRoot>
  );
}

export function AdminDashboardTable<T extends GridValidRowModel>({
  type,
  data,
  columns = [],
  buttonTitle,
  cardTitle,
  identifier,
  handleClick,
  isLoading,
}: {
  type: string;
  buttonTitle?: string;
  /**
   * Optional override for the Card header title. Default is
   * `${toTitleCase(type)}s` (works for types whose plural is the singular
   * + `s`). Pass this when the auto-plural is wrong (e.g. "categorys").
   */
  cardTitle?: string;
  columns?: GridColDef[];
  data?: T[];
  handleClick?: () => void;
  identifier: string;
  isLoading?: boolean;
}) {
  const apiRef = useGridApiRefKeepingPage();
  const title = toTitleCase(type);

  const CustomToolbar = () => (
    <ExpandedQuickFilterToolbar placeholder={`Search ${title}`} />
  );

  return (
    <Card>
      <CardHeader
        title={cardTitle ?? `${title}s`}
        sx={{
          '.MuiCardHeader-title': {
            fontSize: '18px',
          },
        }}
        action={
          <Button
            onClick={() => handleClick?.()}
            variant="outlined"
            size="small"
          >
            {buttonTitle || `Add ${title}`}
          </Button>
        }
      />
      <CardContent>
        <div style={{ height: '272px' }}>
          <DataGridPro
            apiRef={apiRef}
            {...MUI_X_5_GRID_PROPS}
            disableRowSelectionOnClick={true}
            loading={isLoading}
            disableVirtualization={isRunningInJest()}
            rows={data || []}
            getRowId={(row) => `${row[identifier]}`}
            slotProps={{
              ...MUI_X_5_GRID_PROPS.slotProps,
              toolbar: {
                showQuickFilter: true,
                quickFilterProps: { debounceMs: 500 },
              },
            }}
            slots={{
              toolbar: CustomToolbar,
              noRowsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  No {type}s
                </Stack>
              ),
              noResultsOverlay: () => (
                <Stack
                  height="100%"
                  alignItems="center"
                  justifyContent="center"
                >
                  Filter Returned No {type}s
                </Stack>
              ),
            }}
            rowHeight={32}
            columns={columns}
            initialState={INITIAL_STATE}
            pagination
            pageSizeOptions={[5]}
            sx={[
              tableStyleOverrides,
              tableStyleHideSeparator,
              rowDividerOverrides,
            ]}
            showToolbar
          />
        </div>
      </CardContent>
    </Card>
  );
}
